'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Sparkles, LogIn, Globe, User, Crown } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { SUPPORTED_LANGUAGES, getTranslations } from '@/lib/i18n';
import { useAuth } from '@/contexts/AuthContext';

export default function Topbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const { language, setLanguage } = useLanguage();
  const t = getTranslations(language);
  const { user } = useAuth();

  const navItems = [
    { label: t.nav.games, href: '/', key: 'nav-home' },
    { label: t.nav.premium, href: '/#premium', key: 'nav-premium' },
    { label: t.nav.impressum, href: '/impressum', key: 'nav-impressum' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(15,15,26,0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--border)' : 'none',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <AppLogo size={36} />
            <span className="font-extrabold text-xl tracking-tight text-gradient-primary">
              PartyGamesArena
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems?.map((item) => (
              <Link
                key={item?.key}
                href={item?.href}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--muted-foreground)' }}
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Selector */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--muted-foreground)' }}
                aria-label={t.language}
              >
                <Globe size={15} />
                <span>{currentLang?.flag}</span>
                <span className="hidden lg:inline">{currentLang?.label}</span>
              </button>
              {langOpen && (
                <div
                  className="absolute right-0 top-full mt-1 rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px]"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-all hover:bg-white/5 text-left"
                      style={{
                        color: language === lang.code ? 'var(--primary)' : 'var(--foreground)',
                        fontWeight: language === lang.code ? 600 : 400,
                      }}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <>
                <Link
                  href="/subscription"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  <Crown size={16} />
                  Abo
                </Link>
                <Link
                  href="/profile"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  <User size={16} />
                  Profil
                </Link>
              </>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <LogIn size={16} />
                {t.nav.login}
              </Link>
            )}
            <Link
              href="/#premium"
              className="btn-accent flex items-center gap-2 px-4 py-2 text-sm"
            >
              <Sparkles size={16} />
              {t.nav.premium}
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Selector */}
            <div ref={undefined} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="p-2 rounded-lg transition-colors hover:bg-white/5 flex items-center gap-1"
                style={{ color: 'var(--muted-foreground)' }}
                aria-label={t.language}
              >
                <Globe size={18} />
                <span className="text-sm">{currentLang?.flag}</span>
              </button>
              {langOpen && (
                <div
                  className="absolute right-0 top-full mt-1 rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px]"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-all hover:bg-white/5 text-left"
                      style={{
                        color: language === lang.code ? 'var(--primary)' : 'var(--foreground)',
                        fontWeight: language === lang.code ? 600 : 400,
                      }}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="p-2 rounded-lg transition-colors hover:bg-white/5"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menü öffnen"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden animate-fade-in">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="absolute top-16 left-0 right-0 animate-slide-up"
            style={{
              background: 'var(--card)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <nav className="flex flex-col p-4 gap-1">
              {navItems?.map((item) => (
                <Link
                  key={item?.key}
                  href={item?.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                  style={{ color: 'var(--foreground)' }}
                >
                  {item?.label}
                </Link>
              ))}
              <div className="border-t mt-2 pt-2" style={{ borderColor: 'var(--border)' }}>
                {user ? (
                  <>
                    <Link
                      href="/subscription"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all hover:bg-white/5"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      <Crown size={16} />
                      Abonnement
                    </Link>
                    <Link
                      href="/profile"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all hover:bg-white/5"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      <User size={16} />
                      Profil
                    </Link>
                  </>
                ) : (
                  <Link
                    href="/sign-up-login-screen"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all hover:bg-white/5"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    <LogIn size={16} />
                    {t.nav.login}
                  </Link>
                )}
                <Link
                  href="/#premium"
                  onClick={() => setMobileOpen(false)}
                  className="btn-accent flex items-center gap-2 px-4 py-3 mt-2 text-sm justify-center"
                >
                  <Sparkles size={16} />
                  {t.nav.getPremium}
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}